import AllTabs from '@/src/components/Modules-revamp/AllTabs';
import BlockTitle from '@/src/components/Modules-revamp/BlockTitle';
import ContentTabEdit from '@/src/components/Modules-revamp/ContentTabEdit';
import LeaveGapBetween from '@/src/components/Modules-revamp/LeaveGapBetween';
import ModuleWidth from '@/src/components/Modules-revamp/ModuleWidthSelect';
import OverflowItemsDisplayOption from '@/src/components/Modules-revamp/OverflowItemsDisplayOption';
import TabStyle from '@/src/components/Modules-revamp/TabStyle';
import TabType from '@/src/components/Modules-revamp/TabType';
import ContentText from '@/src/components/Modules-revamp/ContentText';
import { Field } from '@/src/components/RcForm';
import { ContentModuleGroupPreloadingContext } from '@/src/constrains/DraggableModules/PreloadComponent/SubscriptionPreloading';
import contentTabRenderTemplate from '@/src/strategies/render/contentTab';
import {
  ResponsiveContainer,
  ResponsiveField,
  formAnimationList,
} from '@/src/strategies/util';
import { EPreviewSize } from '@/src/types/common/SalesChannel';
import { EnumModuleType } from '@/src/types/common/module';
import { get } from 'lodash';
import React from 'react';
import Animation from 'src/components/Modules-revamp/Animation';
import Wrapper from 'src/components/Modules-revamp/BlockWrapper';
import { checkResponsiveContentPageTab } from 'src/constrains/SalesChannelContent-Revamp/ModuleList/validator';
import {
  contentPages as cK,
  contentPagesToShow,
  tabs,
} from 'src/tools/contentModulesFieldName';
import moduleStyleRender from '../../ModuleStyle';
import { useContentTab } from '../../hooks/useContentTab';
type ResponsiveFieldProps = {
  field: ResponsiveField | ResponsiveField[];
  disabled?: boolean;
  sliderDisabled?: boolean;
  options?: Array<{ label: string; value: any }>;
  hasModal?: boolean;
  modalFunc?: any;
  extra?: any;
};

const contentTabRenderer = (form: any, breakpoint: any, extra: any) => {
  const values = form.getFieldsValue(true);
  const { handleOnSectionReset, handleOnSectionCopy, salesChannelMode } = extra;
  const list = contentTabRenderTemplate(values, breakpoint, salesChannelMode);
  const renderComponents = (
    {
      type,
      field,
    }: {
      title: string;
      type?: ResponsiveContainer;
      field: Array<any>;
    },
    breakpoint: any,
  ) => {
    if (type === ResponsiveContainer.ModuleStyle) {
      return field.map((f) => moduleStyleRender(f));
    } else {
      return field.map((f) => contentTabComponent(f, breakpoint, extra));
    }
  };

  return (
    <>
      {list.map((item) => {
        const component = renderComponents(item, breakpoint);
        return (
          <Wrapper key={item.title} id={`${item?.id}`} breakpoint={breakpoint}>
            <BlockTitle
              id={item?.id}
              title={item?.title}
              breakpoint={breakpoint}
              onReset={() => handleOnSectionReset(item?.fields, breakpoint)}
              onCopy={() => handleOnSectionCopy(item?.fields, breakpoint)}
              headerErrorDependencies={get(item, 'headerErrorDependencies')}
              validator={get(item, 'validator')}
            />
            {component}
          </Wrapper>
        );
      })}
    </>
  );
};

const contentTabComponent = (
  data: ResponsiveFieldProps,
  breakpoint: EPreviewSize,
  extra: any,
) => {
  const { animations, hoverAnimations, form, scContentPages, tabValidator } =
    extra || {};
  const { onSort, onDelete, onModalConfirm, onAdd } = extra;
  const draggableId = get(form.getFieldsValue(true), 'draggableId');
  switch (data.field) {
    case ResponsiveField.TabType: {
      return (
        <TabType disabled={data?.disabled} options={data?.options || []} />
      );
    }
    case ResponsiveField.ModuleWidth: {
      return (
        <ModuleWidth disabled={data?.disabled} options={data?.options || []} />
      );
    }
    case ResponsiveField.LeaveGap: {
      return (
        <LeaveGapBetween
          disabled={data?.disabled}
          moduleType={EnumModuleType.ResponsiveContentTab}
        />
      );
    }
    case ResponsiveField.OverflowItems: {
      return (
        <OverflowItemsDisplayOption
          disabled={data?.disabled}
          options={data?.options || []}
        />
      );
    }
    case ResponsiveField.Animation: {
      return (
        <Animation
          disabled={data?.disabled}
          animationOptions={formAnimationList(animations || [])}
          hoverEffectOptions={formAnimationList(hoverAnimations || [])}
        />
      );
    }
    case ResponsiveField.ContentText: {
      return (
        <ContentText
          moduleType={EnumModuleType.ResponsiveProductCarousel}
          disabled={data?.disabled}
        />
      );
    }
    case ResponsiveField.TabStyle: {
      return (
        <TabStyle
          disabled={data?.disabled}
          sliderDisabled={data?.sliderDisabled}
        />
      );
    }
    case ResponsiveField.AllTabs: {
      return (
        <AllTabs
          disabled={data?.disabled}
          draggableId={draggableId}
          onSort={onSort}
          onAdd={onAdd}
          onDelete={onDelete}
          moduleType={EnumModuleType.ResponsivePosterTab}
          breakpoint={breakpoint}
        />
      );
    }
    case ResponsiveField.TabBarSetting: {
      return (
        <>
          <BlockTitle
            disabled={data?.disabled}
            title={data?.extra?.title}
            breakpoint={EPreviewSize.WIDE}
            validator={(d, extra) => {
              const tbs = get(d, tabs);
              const tab = get(tbs, data?.extra?.index);
              return tabValidator(tab, extra);
            }}
            headerErrorDependencies={[
              [tabs, data?.extra?.index, cK],
              [tabs, data?.extra?.index, contentPagesToShow],
            ]}
          />
          <ContentTabEdit
            disabled={data?.disabled}
            options={data?.options || []}
            scContentPages={scContentPages}
            breakpoint={breakpoint}
            index={data?.extra?.index}
            tabDraggableId={data?.extra?.tabDraggableId}
            onSort={onSort}
            onAdd={onAdd}
            onModalConfirm={onModalConfirm}
            onDelete={onDelete}
          />
        </>
      );
    }
  }
};

export default ({ form, breakpoint, ...extra }) => {
  const { handleOnModalConfirm, handleOnAdd, handleOnDelete, handleOnSort } =
    useContentTab();
  const {
    animations,
    hoverAnimations, //options,
    scContentPages,
  } = React.useContext(ContentModuleGroupPreloadingContext);
  const { checkEachTabPagesCountError } = checkResponsiveContentPageTab();
  return (
    <Field>
      {() =>
        contentTabRenderer(form, breakpoint, {
          onModalConfirm: handleOnModalConfirm,
          onAdd: handleOnAdd,
          onDelete: handleOnDelete,
          onSort: handleOnSort,
          // contentOptions: options,
          animations,
          hoverAnimations,
          form,
          scContentPages,
          ...extra,
          tabValidator: checkEachTabPagesCountError,
        })
      }
    </Field>
  );
};
